import { useEffect, useState } from "react";
import {
  Grid as MuiGrid,
  Box as MuiBox,
  Skeleton as MuiSkeleton,
} from "@mui/material";
import { GET_STUDIES_URL_BASE } from "app/shared/constants";
import { useParams } from "react-router-dom";
import { useAlerts } from "common";
import axios from "axios";
import StudyWebsiteContent from "./StudyWebsiteContent";
import { StudyListingPageStyle } from "./StudyListingPageStyle";
import { StudyInterestSection } from "./StudyInterestSection";
import PageTitleListingStatus from "./PageTitleListingStatus";
import PageContent from "app/shared/UI/Layout/PageContent";
import { pageStyles } from "app/shared/UI/Layout/PageStyles";
import StudyRecruitmentSection from "./StudyRecruitmentSection";
import StudyMetaDataSection from "./StudyMetaDataSection";
import StudySearchSection from "./StudySearchSection";
import StudyContactInfoSection from "./StudyContactInfoSection";
import { useTheme } from "@mui/material/styles";
import { useNavBar } from "app/shared/services/NavigationService/NavigationProvider";
import { getStudyTeamNavigation } from "app/services/studiesService";

const studySearchFeatureFlag = false; // Set to true to enable the study search section in the sidebar
const studyMetaDataFeatureFlag = false; // Set to true to enable the study metadata section in the sidebar

export const StudyListingPageContent = () => {
  const { protocolNumber } = useParams();
  const { setAlert } = useAlerts();
  const { addNavItem, clearNavigation } = useNavBar();
  const [studyData, setStudyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const theme = useTheme();
  const styles = StudyListingPageStyle(theme);

  useEffect(() => {
    (async () => {
      if (protocolNumber) {
        setLoading(true);
        try {
          const response = await axios.get(
            `${GET_STUDIES_URL_BASE}/protocol/${protocolNumber}`,
          );
          if (response?.data) {
            setStudyData(response.data);
          }
        } catch (err) {
          console.error("Failed to fetch study", err);
          if (!axios.isCancel(err)) {
            setAlert("error", err.message);
            clearNavigation();
            setError(true);
          }
        }
        setLoading(false);
      }
    })();
  }, [protocolNumber, setAlert, clearNavigation]);

  useEffect(() => {
    if (studyData && !studyData.nctId) {
      setAlert(
        "info",
        "Study data is missing a clinicaltrials.gov NCT number. Please add an NCT number to this protocol in OnCore to enable study website features.",
      );
    }
  }, [studyData, setAlert]);

  useEffect(() => {
    const studyTeamNavItems = getStudyTeamNavigation(protocolNumber);
    studyTeamNavItems.forEach((navItem) => addNavItem(navItem));
    return () => {
      clearNavigation();
    };
  }, [protocolNumber, addNavItem, clearNavigation]);

  return (
    <div style={pageStyles.pageContainer}>
      <div style={pageStyles.pageMain}>
        {!error && (
          <>
            {!loading && (
              <PageTitleListingStatus
                studyId={studyData?.id}
                studyListingStatus={studyData?.studyListingStatus}
                isPublishable={
                  [
                    "OPEN_TO_ACCRUAL",
                    "SUSPENDED",
                    "CLOSED_TO_ACCRUAL",
                    "TERMINATED",
                    "IRB_STUDY_CLOSURE",
                  ].includes(studyData?.protocolStatus) && studyData?.nctId
                }
                protocolStatus={studyData?.protocolStatus}
                nctId={studyData?.nctId}
                protocolNumber={protocolNumber}
                onStatusUpdated={(newStatus) => {
                  setStudyData((prev) => ({
                    ...prev,
                    studyListingStatus: newStatus,
                  }));
                }}
              />
            )}
            {loading && (
              <MuiSkeleton
                variant="text"
                height={75}
                width="50%"
                sx={{ mt: 1, mr: 3, ml: 3, fontSize: "22.5px" }}
              />
            )}

            <PageContent>
              <MuiGrid container spacing={2}>
                <MuiGrid item sx={styles.leftPanel} size={{ xs: 12, sm: 3 }}>
                  {!loading && (
                    <>
                      <StudyRecruitmentSection
                        recruitmentStatus={
                          studyData?.protocolStatus === "OPEN_TO_ACCRUAL"
                        }
                      />

                      <StudyInterestSection
                        studyId={studyData?.id}
                        studySurveyUrl={studyData?.studySurvey}
                      />

                      <StudyContactInfoSection
                        studyId={studyData?.id}
                        studyContacts={studyData?.studyContacts}
                        otherContactEmail={studyData?.recruitmentEmail}
                        otherContactEmailVisibility={studyData?.emailVisibility}
                        otherContactPhone={studyData?.recruitmentPhone}
                        otherContactPhoneVisibility={studyData?.phoneVisibility}
                        mainPointOfContact={studyData?.mainPointOfContact}
                      />

                      {studySearchFeatureFlag && (
                        <StudySearchSection
                          studyId={studyData?.id}
                          conditions={studyData?.conditions}
                          keywords={studyData?.keywords}
                        />
                      )}

                      {studyMetaDataFeatureFlag && (
                        <StudyMetaDataSection
                          age={studyData?.age}
                          gender={studyData?.gender}
                          condition={studyData?.condition}
                          acceptingHealthyVolunteers={
                            studyData?.acceptingHealthyVolunteers
                          }
                        />
                      )}
                    </>
                  )}

                  {loading && (
                    <>
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton
                          variant="circular"
                          width={40}
                          height={40}
                        />
                      </MuiBox>
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton variant="rectangular" height={100} />
                      </MuiBox>
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton height={50} />
                        <MuiSkeleton variant="rectangular" height={150} />
                      </MuiBox>
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton height={50} />
                        <MuiSkeleton height={50} />
                      </MuiBox>
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton variant="rectangular" height={100} />
                        <MuiSkeleton />
                        <MuiSkeleton width="60%" />
                      </MuiBox>
                    </>
                  )}
                </MuiGrid>
                <MuiGrid item sx={styles.mainPanel} size={{ xs: 12, sm: 9 }}>
                  {!loading && <StudyWebsiteContent study={studyData} />}
                  {loading && (
                    <>
                      <MuiSkeleton
                        variant="rectangular"
                        height={300}
                        sx={{ mb: 2 }}
                      />
                      <MuiSkeleton variant="rectangular" height={300} />
                      <MuiBox sx={styles.sideBarSection}>
                        <MuiSkeleton height={50} />
                        <MuiSkeleton variant="rectangular" height={100} />
                        <MuiSkeleton />
                        <MuiSkeleton width="60%" />
                      </MuiBox>
                    </>
                  )}
                </MuiGrid>
              </MuiGrid>
            </PageContent>
          </>
        )}
      </div>
    </div>
  );
};
